import { useAuth } from '../auth/useAuth';
import { useMemo } from 'react';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { getChanceyApiToken } from './authToken';
import { track } from '../services/analyticsService';

export type ApiErrorCode =
  | 'unauthorized'
  | 'rate_limited'
  | 'scan_cap'
  | 'bad_image'
  | 'image_too_large'
  | 'unreadable'
  | 'upstream_failed'
  | 'kill_switch'
  | 'bad_request'
  | 'not_found'
  | 'service_down'
  | 'server_error'
  | 'network_error'
  | 'aborted'
  | 'unknown';

export class ApiError extends Error {
  status: number;
  code: ApiErrorCode;
  requestId?: string;

  constructor(status: number, code: ApiErrorCode, message: string, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

/**
 * Thrown when the server returns 402 with `code: 'scan_cap'`, meaning the user
 * has exhausted their free monthly OCR scan quota. Callers catch this to open
 * the paywall instead of treating it as a generic error.
 */
export class QuotaError extends ApiError {
  readonly used?: number;
  readonly cap?: number | 'unlimited';
  readonly resetsAtUtc?: string;

  constructor(
    message: string,
    data?: { used?: number; cap?: number | 'unlimited'; resetsAtUtc?: string },
    requestId?: string
  ) {
    super(402, 'scan_cap', message, requestId);
    this.name = 'QuotaError';
    this.used = data?.used;
    this.cap = data?.cap;
    this.resetsAtUtc = data?.resetsAtUtc;
  }
}

export interface ApiClient {
  /**
   * POST a JSON body. Pass `undefined` to send no body (the request omits
   * `Content-Type` entirely). Passing `null` sends the literal JSON string
   * `"null"` with `Content-Type: application/json` — only do that when the
   * server explicitly accepts a null payload.
   */
  post<T>(path: string, body: unknown, init?: { signal?: AbortSignal }): Promise<T>;
  put<T>(path: string, body: unknown, init?: { signal?: AbortSignal }): Promise<T>;
  patch<T>(path: string, body: unknown, init?: { signal?: AbortSignal }): Promise<T>;
  delete<T>(path: string, init?: { signal?: AbortSignal }): Promise<T>;
  get<T>(path: string, init?: { signal?: AbortSignal }): Promise<T>;
}

export function useApi(): ApiClient {
  const { getToken } = useAuth();
  const base = process.env.REACT_APP_API_BASE_URL ?? '';

  return useMemo<ApiClient>(() => {
    async function request<T>(
      method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
      path: string,
      body: unknown,
      init?: { signal?: AbortSignal }
    ): Promise<T> {
      if (!base) {
        throw new ApiError(
          0,
          'server_error',
          'REACT_APP_API_BASE_URL is not set; add it to your env file'
        );
      }
      const token = await getChanceyApiToken(getToken);
      const headers: Record<string, string> = {};
      if (token) headers.authorization = `Bearer ${token}`;
      if (body !== undefined) headers['content-type'] = 'application/json';

      let res: Response;
      try {
        res = await fetch(`${base}${path}`, {
          method,
          headers,
          body: body === undefined ? undefined : JSON.stringify(body),
          signal: init?.signal,
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw new ApiError(0, 'aborted', 'Request aborted');
        }
        const message = err instanceof Error ? err.message : 'Network error';
        track('api_error', {
          source: routeTemplate(path),
          outcome: 'network_error',
          errorCode: 'network_error',
        });
        throw new ApiError(0, 'network_error', message);
      }

      // Feed the subscriptionStore with the latest scan quota whenever the
      // server tells us. Applies to ANY response (ok or error) from /v1/ocr/*.
      ingestScanQuotaHeaders(res);

      if (!res.ok) {
        const requestId = res.headers.get('x-chancey-request-id') ?? undefined;
        type ErrBody = {
          error?: { code?: string; message?: string; data?: Record<string, unknown> };
        };
        let parsed: ErrBody = {};
        try {
          parsed = (await res.json()) as ErrBody;
        } catch {
          /* non-JSON error body */
        }
        const code = (parsed.error?.code as ApiErrorCode) ?? mapStatus(res.status);
        const message = parsed.error?.message ?? res.statusText ?? 'Request failed';
        track('api_error', {
          source: routeTemplate(path),
          outcome: String(res.status),
          errorCode: code,
          requestId,
        });
        if (code === 'scan_cap' || res.status === 402) {
          const scans = (parsed.error?.data?.scans ?? {}) as {
            used?: number;
            cap?: number | 'unlimited';
            resetsAtUtc?: string;
          };
          throw new QuotaError(message, scans, requestId);
        }
        throw new ApiError(res.status, code, message, requestId);
      }

      if (res.status === 204) return undefined as unknown as T;
      return (await res.json()) as T;
    }

    return {
      post: (path, body, init) => request('POST', path, body, init),
      put: (path, body, init) => request('PUT', path, body, init),
      patch: (path, body, init) => request('PATCH', path, body, init),
      delete: (path, init) => request('DELETE', path, undefined, init),
      get: (path, init) => request('GET', path, undefined, init),
    };
  }, [base, getToken]);
}

function routeTemplate(path: string): string {
  const pathname = path.split('?')[0] ?? path;
  if (/^\/v1\/me\/tickets\/[^/]+\/?$/.test(pathname)) return '/v1/me/tickets/:id';
  if (/^\/v1\/games\/[^/]+\/rules\/stats\/?$/.test(pathname)) return '/v1/games/:gameId/rules/stats';
  if (/^\/v1\/games\/[^/]+\/stats\/frequency\/?$/.test(pathname)) return '/v1/games/:gameId/stats/frequency';
  if (/^\/v1\/games\/[^/]+\/stats\/pairings\/?$/.test(pathname)) return '/v1/games/:gameId/stats/pairings';
  if (/^\/v1\/games\/[^/]+\/draws\/[^/]+\/?$/.test(pathname)) return '/v1/games/:gameId/draws/:drawDate';
  if (/^\/v1\/games\/[^/]+\/draws\/?$/.test(pathname)) return '/v1/games/:gameId/draws';
  if (/^\/v1\/games\/[^/]+\/?$/.test(pathname)) return '/v1/games/:gameId';
  return pathname;
}

function mapStatus(status: number): ApiErrorCode {
  if (status === 401) return 'unauthorized';
  if (status === 402) return 'scan_cap';
  if (status === 404) return 'not_found';
  if (status === 413) return 'image_too_large';
  if (status === 422) return 'unreadable';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server_error';
  return 'unknown';
}

function ingestScanQuotaHeaders(res: Response): void {
  const cap = res.headers.get('x-chancey-scans-cap');
  const used = res.headers.get('x-chancey-scans-used');
  if (cap === null && used === null) return;
  const normalizedCap: number | 'unlimited' | null =
    cap === null
      ? null
      : cap.toLowerCase() === 'unlimited'
        ? 'unlimited'
        : Number.isFinite(Number(cap))
          ? Number(cap)
          : null;
  const normalizedUsed =
    used === null ? null : Number.isFinite(Number(used)) ? Number(used) : null;
  if (normalizedCap === null && normalizedUsed === null) return;
  const resetAt = res.headers.get('x-chancey-scans-resets-at');
  useSubscriptionStore.getState().setServerQuota({
    used: normalizedUsed ?? 0,
    cap: normalizedCap ?? 'unlimited',
    resetAtUtc: resetAt ?? null,
  });
}
